/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type TooltipPrimitive = string | number | boolean;

type TooltipField<TData> = {
  /** Display label for the tooltip field */
  label: string;
  /** Extract a raw value from the source data */
  getValue: (data: TData) => TooltipPrimitive | null | undefined;
  /** Optional formatter for non-empty values */
  format?: (value: TooltipPrimitive) => string;
};

/* -------------------------------------------------------------------------- */
/*                                   Utility                                  */
/* -------------------------------------------------------------------------- */

/**
 * Build a newline-separated tooltip from an ordered list of field definitions.
 *
 * Fields with null/undefined/empty-string values are omitted.
 * Numeric zero and boolean false are preserved.
 */
const formatTooltipFromFields = <TData>(data: TData, fields: TooltipField<TData>[]): string => {
  return fields
    .map((field) => {
      const value = field.getValue(data);
      if (value == null || value === '') return null;

      const formattedValue = field.format ? field.format(value) : String(value);
      return `${field.label}: ${formattedValue}`;
    })
    .filter((line): line is string => line !== null)
    .join('\n');
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export type { TooltipField, TooltipPrimitive };
export { formatTooltipFromFields };
